// Bottle Sort - DOM rendering, tap interaction, and the GameDef export.

import { el, clear } from "../../lib/dom";
import { sfx, haptic } from "../../lib/feedback";
import type { GameDef, LevelView, LevelViewOptions } from "../types";
import {
  type State,
  type WsLevel,
  canPour,
  cloneState,
  generateWsLevel,
  isSolved,
  optionsForWsLevel,
  pour,
  removeServed,
  slotsFor,
  solveFrom,
} from "./engine";
import "./watersort.css";

// Endless: every level is generated on demand, deterministically from its
// number, and PROVEN solvable by the solver before it is ever shown.
const levelCache = new Map<number, WsLevel>();
function levelForNumber(n: number): WsLevel {
  let lvl = levelCache.get(n);
  if (!lvl) {
    lvl = generateWsLevel(optionsForWsLevel(n));
    levelCache.set(n, lvl);
  }
  return lvl;
}

function starsFor(moves: number, par: number): number {
  if (moves <= par) return 3;
  if (moves <= Math.ceil(par * 1.5)) return 2;
  return 1;
}

function createLevelView(opts: LevelViewOptions): LevelView {
  const level = levelForNumber(opts.level);
  const par = level.par;

  let state: State = slotsFor(level.bottles);
  let moves = 0;
  let selected = -1; // slot id
  let finished = false;
  let busy = false; // during the serve animation
  let history: Array<{ state: State; moves: number }> = [];
  const timers: Array<ReturnType<typeof setTimeout>> = [];

  const root = opts.mount;
  clear(root);
  root.classList.add("ws");

  const boardEl = el("div.ws-board");
  const stage = el("div.ws-stage", {}, boardEl);
  const moveLabel = el("span.ws-moves");
  const toolbar = el(
    "div.ws-toolbar",
    {},
    el("button.ws-btn", { onclick: doHint, "aria-label": "Hint" }, "💡 Hint"),
    el("button.ws-btn", { onclick: doUndo, "aria-label": "Zet terug" }, "↩ Terug"),
    moveLabel,
    el("button.ws-btn", { onclick: doRestart, "aria-label": "Opnieuw" }, "↺ Opnieuw"),
  );
  root.append(stage, toolbar);

  let overlay: HTMLElement | null = null;

  function later(fn: () => void, ms: number): void {
    timers.push(setTimeout(fn, ms));
  }

  // ---- Rendering ----
  function bottleEl(slotId: number, colors: number[], extra: { pouredIn?: number; served?: boolean }): HTMLElement {
    const node = el(
      `div.ws-bottle${selected === slotId ? ".selected" : ""}${extra.served ? ".served" : ""}`,
      { "data-id": slotId },
    );
    const liquid = el("div.ws-liquid");
    colors.forEach((c, idx) => {
      // Color index maps onto the global palette (shop-cosmetica); css tints it.
      const unit = el(`div.ws-unit.ws-c-${(c % 9) + 1}`);
      // Newly poured units (the top `pouredIn` of the stack) splash in.
      if (extra.pouredIn && idx >= colors.length - extra.pouredIn) unit.classList.add("ws-new");
      liquid.append(unit);
    });
    node.append(liquid, el("div.ws-glass"));
    node.addEventListener("pointerdown", (e) => {
      e.preventDefault();
      onBottle(slotId);
    });
    return node;
  }

  function render(effect: { pouredTo?: number; pouredIn?: number; servedId?: number } = {}): void {
    clear(boardEl);
    for (const s of state) {
      boardEl.append(
        bottleEl(s.id, s.colors, {
          pouredIn: s.id === effect.pouredTo ? effect.pouredIn : 0,
          served: s.id === effect.servedId,
        }),
      );
    }
    updateStatus();
  }

  function updateStatus(): void {
    moveLabel.textContent = `Zetten: ${moves} · Par ${par}`;
    const remaining = state.filter((s) => s.colors.length > 0).length;
    opts.setStatus?.(`🧪 ${level.colors - remaining >= 0 ? level.colors - remaining : 0}/${level.colors}`);
  }

  // ---- Interaction ----
  function onBottle(id: number): void {
    if (finished || busy) return;
    const idx = state.findIndex((s) => s.id === id);
    if (idx < 0) return;

    if (selected === -1) {
      if (state[idx].colors.length === 0) return;
      selected = id;
      sfx.tap();
      haptic(4);
      render();
      return;
    }
    if (selected === id) {
      selected = -1;
      render();
      return;
    }
    const si = state.findIndex((s) => s.id === selected);
    if (si >= 0 && canPour(state, si, idx)) {
      doPour(si, idx);
    } else if (state[idx].colors.length > 0) {
      selected = id; // switch selection
      sfx.tap();
      render();
    } else {
      sfx.error();
    }
  }

  function doPour(si: number, ti: number): void {
    const targetId = state[ti].id;
    history.push({ state: cloneState(state), moves });
    const res = pour(state, si, ti);
    if (!res) return;
    moves++;
    selected = -1;
    state = res.state;

    if (res.servedId != null) {
      busy = true;
      sfx.clear();
      haptic([12, 24, 12]);
      render({ pouredTo: targetId, pouredIn: res.moved, servedId: res.servedId });
      later(() => {
        state = removeServed(state, res.servedId!);
        busy = false;
        render();
        afterMove();
      }, 450);
    } else {
      sfx.place();
      haptic(6);
      render({ pouredTo: targetId, pouredIn: res.moved });
      afterMove();
    }
  }

  function afterMove(): void {
    if (isSolved(state)) {
      win();
      return;
    }
    // Softlock guard: the solver PROVES after every move that the level can
    // still be finished. Because this runs on each move, the previous state in
    // the history is always still solvable - so recovering is exactly one
    // undo. This also covers the hard case (no legal moves at all).
    if (!solveFrom(state)) showSoftlock();
  }

  // ---- Win / softlock ----
  function win(): void {
    if (finished) return;
    finished = true;
    sfx.win();
    haptic([20, 40, 20]);
    later(() => opts.onWin({ stars: starsFor(moves, par), moves }), 400);
  }

  function showSoftlock(): void {
    sfx.lose();
    haptic([10, 30, 10]);
    overlay = el(
      "div.ws-overlay",
      {},
      el(
        "div.ws-overlay-card",
        {},
        el("h3", {}, "Vastgelopen! 🍾"),
        el("p", {}, "Vanaf hier is het level niet meer op te lossen. Je laatste zet wordt teruggedraaid."),
        el("button.ws-btn.primary", { onclick: () => doUndo() }, "↩ Oké, zet terug"),
        el("button.ws-btn", { onclick: doRestart }, "↺ Helemaal opnieuw"),
      ),
    );
    root.append(overlay);
  }

  function hideOverlay(): void {
    overlay?.remove();
    overlay = null;
  }

  // ---- Toolbar ----
  function doUndo(): void {
    if (finished || busy || history.length === 0) return;
    hideOverlay();
    const prev = history.pop()!;
    state = prev.state;
    moves = prev.moves;
    selected = -1;
    sfx.slide();
    render();
  }

  function doHint(): void {
    if (finished || busy) return;
    const sol = solveFrom(state);
    if (!sol || sol.length === 0) {
      sfx.error();
      return;
    }
    sfx.tap();
    boardEl.querySelector<HTMLElement>(`.ws-bottle[data-id="${sol[0].from}"]`)?.classList.add("hinted", "hinted-from");
    boardEl.querySelector<HTMLElement>(`.ws-bottle[data-id="${sol[0].to}"]`)?.classList.add("hinted", "hinted-to");
    later(() => {
      boardEl
        .querySelectorAll(".ws-bottle.hinted")
        .forEach((n) => n.classList.remove("hinted", "hinted-from", "hinted-to"));
    }, 1000);
  }

  function doRestart(): void {
    hideOverlay();
    state = slotsFor(level.bottles);
    moves = 0;
    selected = -1;
    finished = false;
    busy = false;
    history = [];
    render();
  }

  // ---- Init ----
  render();

  return {
    destroy() {
      for (const t of timers) clearTimeout(t);
    },
    restart: doRestart,
  };
}

export const waterSort: GameDef = {
  id: "watersort",
  title: "Bottle Sort",
  tagline: "Giet en sorteer, serveer elk flesje",
  accent: "#e2761c",
  icon: "🧪",
  levelCount: Infinity,
  createLevelView,
};
